import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { getPageTitle } from "../../utils/getPageTitle";
import FallbackImage from "../../components/common/fallbackImage";
import Button from "../../components/common/button";
import CategoryModal from "../../components/modal/categoryModal";
import Datatable from "../../components/common/datatable";
import { useViewCategory } from "../../hooks/categorys/category.showall";
import { useRemoveCategory } from "../../hooks/categorys/category.delete";
import { useCategoryStore } from "../../hooks/categorys/category.store";
import { useCategoryUpdate } from "../../hooks/categorys/category.update";
import SearchItem from "../../components/searchItem";
import ActionMenu from "../../components/common/menu";
import Pagination from "../../components/common/pagination";
import DeleteModal from "../../components/modal/deleteModal";
import "../../assets/styles/products.css";
import "../../assets/styles/pagination.css";

const Categories = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editCategory, setEditCategory] = useState(null);
  const [modalKey, setModalKey] = useState(0);
  const { mutate: addCategory, isPending: isAdding } = useCategoryStore();
  const { mutate: updateCategory, isPending: isUpdating } = useCategoryUpdate();
  const [mode, setMode] = useState("create");
  const [localCategories, setLocalCategories] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalDocs: 0,
    limit: pageSize,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const {
    data: categoriesResponse,
    isLoading,
    isFetching,
    isError,
    refetch: refetchCategories,
  } = useViewCategory({
    page,
    limit: pageSize,
    q: debouncedSearchTerm,
  });
  const { mutate: removeCategory, isPending: isDeleting } = useRemoveCategory();
  const location = useLocation();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (!categoriesResponse) return;

    setLocalCategories(categoriesResponse.categories ?? []);

    const apiPagination = categoriesResponse.pagination;
    if (apiPagination) {
      setPagination({
        currentPage: apiPagination.currentPage || 1,
        totalPages: apiPagination.totalPages || 1,
        totalDocs: apiPagination.total || 0,
        limit: apiPagination.limit || pageSize,
        hasNextPage: apiPagination.hasNextPage || false,
        hasPrevPage: apiPagination.hasPrevPage || false,
      });
    }
  }, [categoriesResponse, pageSize]);

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setPage(1);
  };

  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const handleAddIndustry = () => {
    setEditCategory(null);
    setMode("create");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleEdit = (category) => {
    setEditCategory(category);
    setMode("edit");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleView = (category) => {
    setEditCategory(category);
    setMode("view");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleDelete = (category) => {
    setEditCategory(category);
    setMode("delete");
    setModalKey((k) => k + 1);
    setIsModalOpen(true);
  };

  const handleSave = (payload) => {
    const mutationOptions = {
      onSuccess: () => {
        setIsModalOpen(false);
        setEditCategory(null);
        refetchCategories();
      },
      onError: (err) => {
        alert(
          `Operation failed: ${err.response?.data?.message || err.message || "Please try again."}`,
        );
      },
    };

    if (mode === "delete") {
      removeCategory(editCategory._id || editCategory.id, mutationOptions);
      return;
    }

    const body = new FormData();
    body.append("title", payload.title || "");
    body.append("slug", payload.slug || "");
    body.append("description", payload.description || "");
    body.append("isActive", payload.isActive ?? true);

    if (payload.image instanceof File) {
      body.append("image", payload.image);
    }

    if (Array.isArray(payload.catlogs)) {
      payload.catlogs.forEach((cat, index) => {
        if (cat._id) {
          body.append(`catlogs[${index}]._id`, cat._id);
        }
        body.append(`catlogs[${index}].title`, cat.title || "");
        body.append(`catlogs[${index}].description`, cat.description || "");
        if (cat.existingFile && !cat.file) {
          body.append(`catlogs[${index}].existingFile`, cat.existingFile);
        }
        if (cat.file) {
          body.append("catlogs", cat.file);
        }
      });
    }

    if (Array.isArray(payload.deletedCatlogIds)) {
      payload.deletedCatlogIds.forEach((id) => {
        body.append("deletedCatlogIds[]", id);
      });
    }

    if (mode === "edit" && editCategory) {
      updateCategory(
        {
          id: editCategory._id || editCategory.id,
          body: body,
        },
        mutationOptions,
      );
    } else {
      addCategory(body, mutationOptions);
    }
  };

  const columns = [
    {
      key: "image",
      label: "Image",
      render: (value) => {
        if (!value) return "-";
        return (
          <FallbackImage
            src={value}
            alt="Logo"
            style={{
              width: 40,
              height: 40,
              objectFit: "cover",
              borderRadius: "4px",
            }}
          />
        );
      },
    },
    { key: "title", label: "Title" },
    { key: "description", label: "Description" },
    {
      key: "isActive",
      label: "Status",
      render: (_, row) => {
        const active =
          row.isActive === true ||
          row.isActive === "Active" ||
          row.isActive === 1 ||
          row.status === true ||
          row.status === "Active" ||
          row.status === 1;
        return (
          <span
            className={`status-badge ${active ? "status-active" : "status-expired"}`}
          >
            {active ? "Active" : "Inactive"}
          </span>
        );
      },
    },
    {
      key: "actions",
      label: "Actions",
      render: (_, row) => (
        <ActionMenu
          row={row}
          onView={handleView}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      ),
    },
  ];

  if (isLoading) return <div>Loading...</div>;
  if (isError) return <div>Error loading categories</div>;

  return (
    <>
      <div className="page-body">
        <div className="page-header">
          <h1 className="page-title">{getPageTitle(location.pathname)}</h1>
          <p style={{ color: "rgb(85, 85, 85)", fontSize: "13px" }}>
            Manage category categories
          </p>
        </div>
        <Button onClick={handleAddIndustry}>+ Add Category</Button>
      </div>

      <div className="list-toolbar">
        <div className="list-toolbar-row">
          <SearchItem
            data={localCategories}
            searchField={["title", "description"]}
            placeholder="Search categories..."
            wrapSection={false}
            onResultsChange={({ searchItem }) => {
              setSearchTerm(searchItem);
            }}
          />

          <label className="list-toolbar-page-size">
            <span>Items per page:</span>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
            </select>
          </label>
        </div>
      </div>

      <CategoryModal
        key={modalKey}
        isOpen={isModalOpen && mode !== "delete"}
        onClose={() => setIsModalOpen(false)}
        editData={editCategory}
        mode={mode}
        viewMode={mode === "view"}
        onSubmit={handleSave}
        isLoading={isAdding || isUpdating}
      />

      <DeleteModal
        isOpen={isModalOpen && mode === "delete"}
        onClose={() => {
          setIsModalOpen(false);
          setEditCategory(null);
        }}
        onConfirm={() => handleSave()}
        title="Delete Category"
        itemName={editCategory?.title}
        isLoading={isDeleting}
      />

      <Datatable data={localCategories} columns={columns} />

      {isFetching && !isLoading && (
        <p style={{ fontSize: 12, color: "#888", marginTop: 8 }}>Updating...</p>
      )}

      <Pagination
        currentPage={page}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default Categories;
